import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarClock, PackageCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { PreorderProductRow, PreorderOrdersList, ReceiveModal } from '../../components/admin/PreorderSections';
import { LowStockPreorderPanel } from '../../components/admin/LowStockPreorderPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPreorders = () => {
  const { isStaff } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ products: [], orders: [], due_count: 0, today: '' });
  const [loading, setLoading] = useState(true);
  const [receiving, setReceiving] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/preorders/overview`);
      setData(res.data);
    } catch {
      toast.error('Impossible de charger les précommandes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/connexion'); return; }
    load();
  }, [isStaff, navigate, load]);

  const postpone = async (product, newDate) => {
    try {
      await axios.put(`${API_URL}/api/admin/preorders/${product.id}/postpone`, { expected_date: newDate });
      toast.success('Date reportée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du report');
    }
  };

  const receive = async (qty) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/preorders/${receiving.id}/receive`, { received_qty: qty });
      const d = res.data;
      toast.success(`Réception validée — stock ${d.stock} · ${d.orders_converted} précommande(s) passée(s) en préparation`);
      if (d.orders_short > 0) toast.warning(`${d.orders_short} précommande(s) non couverte(s) par la quantité reçue`);
      setReceiving(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la réception');
    }
  };

  if (!isStaff) return null;

  const reservedFor = (pid) => data.orders.filter(o => o.items.some(i => i.product_id === pid)).length;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Tableau de bord
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[color:var(--color-primary)] text-xs uppercase tracking-[0.3em] mb-2">Arrivages fournisseurs</p>
            <h1 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Précommandes
            </h1>
          </div>
          <Link to="/admin/produits/nouveau"
            className="inline-flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-sm hover:bg-[color:var(--color-primary-hover)]"
            data-testid="preorder-new-product-btn">
            <Plus className="w-4 h-4" /> Nouveau produit à venir
          </Link>
        </div>

        {data.due_count > 0 && (
          <div className="mb-6 border border-amber-500/40 bg-amber-500/10 p-4 flex items-center gap-3" data-testid="preorders-due-alert">
            <CalendarClock className="w-5 h-5 text-amber-400" />
            <p className="text-amber-200 text-sm">
              {data.due_count} arrivage(s) dont la date est atteinte — validez la réception ou reportez la date.
            </p>
          </div>
        )}

        <section className="mb-10">
          <h2 className="text-white text-lg mb-3 flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-[color:var(--color-primary)]" /> Produits attendus ({data.products.length})
          </h2>
          {loading ? (
            <p className="text-zinc-500 text-sm">Chargement...</p>
          ) : data.products.length === 0 ? (
            <p className="text-zinc-500 text-sm border border-white/10 p-6 text-center" data-testid="preorders-empty">
              Aucun produit en précommande. Activez « Précommande fournisseur » sur une fiche produit.
            </p>
          ) : (
            <ul className="space-y-3" data-testid="preorder-products-list">
              {data.products.map(p => (
                <PreorderProductRow key={p.id} product={p} today={data.today}
                  ordersCount={reservedFor(p.id)}
                  onReceive={() => setReceiving(p)} onPostpone={postpone} />
              ))}
            </ul>
          )}
        </section>

        <PreorderOrdersList orders={data.orders} />

        <div className="mt-10">
          <LowStockPreorderPanel onChanged={load} />
        </div>
      </div>

      {receiving && (
        <ReceiveModal product={receiving} onClose={() => setReceiving(null)} onConfirm={receive} />
      )}
    </div>
  );
};

export default AdminPreorders;
